/**
 * Phone utility functions for handling phone number formatting and conversion
 */

/**
 * Converts a phone number to E.164 format for database storage
 * @param {string} phoneNumber - National format phone number
 * @param {string} countryCode - Country code (e.g., "+1")
 * @returns {string} E.164 formatted phone number
 */
export const toE164Format = (phoneNumber, countryCode) => {
  if (!phoneNumber || !countryCode) return phoneNumber;

  // Remove all non-digit characters
  const cleanPhone = String(phoneNumber).replace(/\D/g, '');
  const cleanCountryCode = countryCode.replace(/\D/g, '');

  // Don't add country code if it's already present
  if (cleanPhone.startsWith(cleanCountryCode)) {
    return cleanPhone;
  }

  return cleanCountryCode + cleanPhone;
};

/**
 * Converts E.164 format to national format for display
 * @param {string} e164Number - E.164 format phone number
 * @param {string} countryCode - Country code (e.g., "+1")
 * @returns {string} National format phone number
 */
export const fromE164Format = (e164Number, countryCode) => {
  if (!e164Number || !countryCode) return e164Number;

  const cleanE164 = String(e164Number).replace(/\D/g, '');
  const cleanCountryCode = countryCode.replace(/\D/g, '');

  // Remove country code if present
  if (cleanE164.startsWith(cleanCountryCode)) {
    return cleanE164.slice(cleanCountryCode.length);
  }

  return cleanE164;
};

/**
 * Formats a phone number for display using business preferences
 * @param {string} phoneNumber - Phone number in national format (digits only)
 * @param {Object} [preferences] - Business preferences object (optional)
 * @param {string} [preferences.phoneNumberFormat] - Format pattern (e.g., "(###) ###-####")
 * @param {string} [preferences.phoneNumberCountryCode] - Country code (e.g., "+1")
 * @returns {Object} Formatted phone display object with displayText and rawValue
 */
export const formatPhoneForDisplay = (phoneNumber, preferences) => {
  if (!phoneNumber) {
    return { displayText: '', rawValue: '' };
  }

  // If we have preferences with phone format, use it to display
  if (preferences?.phoneNumberFormat) {
    // Remove all non-digits from the phone number
    const digits = String(phoneNumber).replace(/\D/g, '');

    // Apply the format pattern by replacing # with digits
    let formatted = '';
    let digitIndex = 0;

    for (let i = 0; i < preferences.phoneNumberFormat.length; i++) {
      if (preferences.phoneNumberFormat[i] === '#') {
        if (digitIndex < digits.length) {
          formatted += digits[digitIndex];
          digitIndex++;
        } else {
          // If we run out of digits, stop adding more
          break;
        }
      } else {
        formatted += preferences.phoneNumberFormat[i];
      }
    }

    // If we have a country code preference, include it in display
    // for now do not add country code
    const displayText = formatted;

    return {
      displayText,
      rawValue: digits
    };
  }

  // Fallback: return raw digits if no preferences available
  return {
    displayText: phoneNumber,
    rawValue: String(phoneNumber).replace(/\D/g, '')
  };
};